import { Controller } from '@hotwired/stimulus';

const STATUS_TARGET = /^status_ai_message_\d+$/;
const MESSAGE_TARGET = /^ai_message_\d+$/;

/**
 * The Hey Amba composer: Enter submits (Shift+Enter inserts a newline,
 * Cmd/Ctrl+Enter always submits), and the send button is disabled with a
 * spinner while a response streams.
 *
 * Busy lifecycle: submitting replaces the whole composer server-side, so the
 * replacement form renders its own initial busy state (`busy` value +
 * disabled button) and this controller re-arms on connect. Completion is
 * detected from the turbo-stream broadcasts: a replace of `ai_message_<id>`
 * whose template contains a rendered assistant answer ends the busy state.
 * A failed run never broadcasts an answer (the pending frame is re-rendered),
 * so an idle timeout — reset by every status/progress broadcast for this
 * chat — unlocks the composer after sustained silence.
 */
export default class extends Controller {
  static targets = ['input', 'submit', 'spinner', 'sendIcon'];
  static values = {
    busy: Boolean,
    enterSubmits: Boolean,
    idleTimeout: { type: Number, default: 120_000 },
    maxRows: { type: Number, default: 8 },
  };

  connect() {
    this.onStream = this.onStream.bind(this);
    this.onSubmitStart = this.onSubmitStart.bind(this);
    this.onSubmitEnd = this.onSubmitEnd.bind(this);
    document.addEventListener('turbo:before-stream-render', this.onStream);
    this.element.addEventListener('turbo:submit-start', this.onSubmitStart);
    this.element.addEventListener('turbo:submit-end', this.onSubmitEnd);
    if (this.busyValue) this.startBusy();
    this.autogrow();
  }

  disconnect() {
    document.removeEventListener('turbo:before-stream-render', this.onStream);
    this.element.removeEventListener('turbo:submit-start', this.onSubmitStart);
    this.element.removeEventListener('turbo:submit-end', this.onSubmitEnd);
    this.clearIdleTimer();
  }

  handleKeydown(event) {
    if (event.key !== 'Enter' || event.isComposing) return;

    const modifier = event.metaKey || event.ctrlKey;
    const plain =
      !event.shiftKey && !modifier && !event.altKey && this.enterSubmitsValue;
    if (!modifier && !plain) return;

    event.preventDefault();
    if (this.busy) return;
    if (this.hasInputTarget && this.inputTarget.value.trim() === '') return;

    this.element.requestSubmit();
  }

  onSubmitStart() {
    this.startBusy();
    if (this.hasInputTarget) this.inputTarget.value = '';
    this.autogrow();
  }

  // Sizes the textarea to its content, up to `maxRows` lines, after which it
  // becomes its own scroll region. Opt-in per textarea (`data-ai-chat-autogrow`)
  // because this controller is shared with the drawer composers, whose `rows: 3`
  // field must not be shrunk on connect.
  autogrow() {
    if (!this.autogrowEnabled) return;

    const input = this.inputTarget;
    const style = getComputedStyle(input);
    const lineHeight =
      parseFloat(style.lineHeight) || parseFloat(style.fontSize);
    const padding =
      parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const max = lineHeight * this.maxRowsValue + padding;

    input.style.height = 'auto';
    const content = input.scrollHeight;
    input.style.height = `${Math.min(content, max)}px`;
    input.style.overflowY = content > max ? 'auto' : 'hidden';
  }

  get autogrowEnabled() {
    return (
      this.hasInputTarget && this.inputTarget.dataset.aiChatAutogrow === 'true'
    );
  }

  onSubmitEnd(event) {
    if (!event.detail.success) this.endBusy();
  }

  onStream(event) {
    const target = event.target.target || '';
    const existing = document.getElementById(target);
    if (existing && !this.sameChat(existing)) return;

    if (STATUS_TARGET.test(target)) {
      this.resetIdleTimer();
      return;
    }
    if (!MESSAGE_TARGET.test(target) || event.target.action !== 'replace')
      return;

    const answered = event.target.templateElement?.content?.querySelector(
      '.amba-ai-message-card--assistant .amba-markdown'
    );
    if (answered) {
      this.endBusy();
    } else {
      // A failure re-renders the pending frame (no answer): stay busy but keep
      // the idle timer running so the composer eventually unlocks.
      this.resetIdleTimer();
    }
  }

  // The replaced element must belong to THIS chat surface — the drawer and the
  // full-page thread can both subscribe to the same broadcasts.
  sameChat(element) {
    const root = element.closest('.amba-ai-chat, .amba-ai-chat__content');
    return root ? root.contains(this.element) : true;
  }

  startBusy() {
    this.busy = true;
    if (this.hasSubmitTarget) this.submitTarget.disabled = true;
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove('d-none');
    if (this.hasSendIconTarget) this.sendIconTarget.classList.add('d-none');
    this.resetIdleTimer();
  }

  endBusy() {
    this.busy = false;
    if (this.hasSubmitTarget) this.submitTarget.disabled = false;
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add('d-none');
    if (this.hasSendIconTarget) this.sendIconTarget.classList.remove('d-none');
    this.clearIdleTimer();
  }

  resetIdleTimer() {
    if (!this.busy) return;
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => this.endBusy(), this.idleTimeoutValue);
  }

  clearIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}
